import {
  Component,
  computed,
  inject,
  input,
  output,
  SecurityContext,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  NotificationDto,
  NotificationType,
  TopicType
} from '@bannerflow/types.notification-service';
import {
  UIAvatarComponent,
  UIComponentSizeDirective,
  UILabelComponent,
  UILinkDirective,
  UINUIIcon,
  UISVGIconComponent,
  UITooltipDirective,
  UITypographyDirective
} from '@bannerflow/ui';
import { AvatarColorPipe } from '../../../pipes/avatar-color.pipe';
import { NotificationService } from '../../../services/notification.service';
import {
  downloadLink,
  getAdScanningFinishedTitle,
  getAiVideoGenerationTitle,
  getExportSourceName,
  getImageExportDescription,
  getImageExportTitle,
  getLinkToCreativeSet,
  getPredictiveScoringLinkCaption,
  getPredictiveScoringTitle,
  getSenderId,
  getVideoExportDescription,
  getVideoExportTitle,
  isAdScanningFinishedNotification,
  isAiVideoGenerationNotification,
  getAiVideoGenerationDescriptionContent,
  isCommentAddedNotification,
  isImageExportNotification,
  isPredictiveScoringCompletedNotification,
  isResourceAccessApprovedNotification,
  isResourceAccessRejectedNotification,
  isResourceAccessRequestedNotification,
  isSystemNotification,
  isThreadArchivedNotification,
  isThreadReopenedNotification,
  isVideoExportNotification
} from '../../../utils';
import { NotificationClickEvent } from '../../../notification.types';
import { timeSince } from '../../../utils/time.utils';
import { ReadStatusMarkerComponent } from './read-status-marker/read-status-marker.component';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
  imports: [
    AvatarColorPipe,
    ReadStatusMarkerComponent,
    UIAvatarComponent,
    UIComponentSizeDirective,
    UILabelComponent,
    UILinkDirective,
    UISVGIconComponent,
    UITooltipDirective,
    UITypographyDirective
  ],
  selector: 'ns-notification-item',
  templateUrl: './notification-item.component.html',
  styleUrl: './notification-item.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    '[attr.tabindex]': '0',
    '[class]': 'gainsightSelector()',
    '(keypress.enter)': 'openLink()',
    '(keypress.space)': 'openLink()',
    '(click)': 'openLink()'
  }
})
export class NotificationItemComponent {
  private domSanitizer = inject(DomSanitizer);
  private notificationService = inject(NotificationService);

  notification = input.required<NotificationDto>();

  linkClicked = output();
  notificationClicked = output<NotificationClickEvent>();

  actionIcon = computed(() => this.computeActionIcon());
  actorName = computed(() => this.computeActorName());
  avatarText = computed(() => this.computeAvatarText());
  createdTime = computed(() => this.computeCreatedTime());
  descriptionContent = computed(() => this.computeDescriptionContent());
  link = computed(() => this.computeLink());
  linkCaption = computed(() => this.computeLinkCaption());
  senderId = computed(() => getSenderId(this.notification()));
  sourceName = computed(() => this.computeSourceName());
  timeSince = computed(() => this.computeTimeSince());
  title = computed(() => this.computeTitle());

  gainsightSelector = computed(() => {
    const notification = this.notification();
    switch (notification.topic) {
      case TopicType.ImageExport:
        return 'image-export-notification';
      case TopicType.VideoExport:
        return 'video-export-notification';
      case TopicType.CommentAdded:
        return 'comment-added-notification';
      case TopicType.ThreadArchived:
        return 'thread-archived-notification';
      case TopicType.ThreadReopened:
        return 'thread-reopened-notification';
      case TopicType.AdScanningFinished:
        return 'ad-scanning-notification';
      case TopicType.PredictiveScoringCompleted:
        return 'predictive-scoring-notification';
      default:
        return `${notification.topic}-notification`;
    }
  });

  openLink(): void {
    const notification = this.notification();
    const link = notification.content.link;
    if (!link) {
      return;
    }

    const isPrevented = this.emitNotificationClicked(notification);
    if (!isPrevented) {
      this.navigateToNotification(notification, link);
    }

    this.notificationService.markAsRead(notification);
    this.linkClicked.emit();
  }

  handleCaptionClick(event: MouseEvent): void {
    event.stopPropagation();
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }

    const notification = this.notification();
    const isPrevented = this.emitNotificationClicked(notification);
    if (!isPrevented) {
      // Leave the href to the browser, as this anchor always has.
      return;
    }

    event.preventDefault();
    this.notificationService.markAsRead(notification);
    this.linkClicked.emit();
  }

  private emitNotificationClicked(notification: NotificationDto): boolean {
    let isPrevented = false;
    this.notificationClicked.emit({
      notification,
      preventDefault: () => {
        isPrevented = true;
      }
    });
    return isPrevented;
  }

  private navigateToNotification(
    notification: NotificationDto,
    link: string
  ): void {
    if (
      isVideoExportNotification(notification) ||
      isImageExportNotification(notification)
    ) {
      downloadLink(link);
    } else if (
      isCommentAddedNotification(notification) ||
      isThreadArchivedNotification(notification) ||
      isThreadReopenedNotification(notification)
    ) {
      const creativeSetLink = this.link();
      if (creativeSetLink) {
        if (creativeSetLink.startsWith('#')) {
          window.location.hash = creativeSetLink;
        } else {
          this.openLinkInNewTab(creativeSetLink);
        }
      }
    } else {
      this.openLinkInNewTab(link);
    }
  }

  private computeTitle(): string {
    const notification = this.notification();

    if (isVideoExportNotification(notification)) {
      return getVideoExportTitle(notification);
    } else if (isImageExportNotification(notification)) {
      return getImageExportTitle(notification);
    } else if (isCommentAddedNotification(notification)) {
      return 'Commented';
    } else if (isThreadArchivedNotification(notification)) {
      return 'Archived thread';
    } else if (isThreadReopenedNotification(notification)) {
      return 'Reopened thread';
    } else if (isAdScanningFinishedNotification(notification)) {
      return getAdScanningFinishedTitle(notification);
    } else if (isResourceAccessRequestedNotification(notification)) {
      return 'Access requested';
    } else if (isResourceAccessApprovedNotification(notification)) {
      return 'Access granted';
    } else if (isResourceAccessRejectedNotification(notification)) {
      return 'Access denied';
    } else if (isAiVideoGenerationNotification(notification)) {
      return getAiVideoGenerationTitle(notification);
    } else if (isPredictiveScoringCompletedNotification(notification)) {
      return getPredictiveScoringTitle(notification);
    }

    return '';
  }

  private computeSourceName(): string {
    const notification = this.notification();

    if (
      isVideoExportNotification(notification) ||
      isImageExportNotification(notification)
    ) {
      return getExportSourceName(notification);
    } else if (
      isCommentAddedNotification(notification) ||
      isThreadArchivedNotification(notification) ||
      isThreadReopenedNotification(notification)
    ) {
      return notification.content.metadata.creativeSetName;
    } else if (isAdScanningFinishedNotification(notification)) {
      return notification.content.metadata.campaignName ?? '';
    } else if (isResourceAccessRequestedNotification(notification)) {
      return notification.content.metadata.resourceName ?? '';
    } else if (isResourceAccessApprovedNotification(notification)) {
      return notification.content.metadata.resourceName ?? '';
    } else if (isResourceAccessRejectedNotification(notification)) {
      return notification.content.metadata.resourceName ?? '';
    } else if (isAiVideoGenerationNotification(notification)) {
      return (
        notification.content.metadata['error'] ??
        notification.content.metadata.name ??
        ''
      );
    } else if (isPredictiveScoringCompletedNotification(notification)) {
      return notification.content.metadata.creativeSetName ?? '';
    }
    return '';
  }

  private computeLink(): string | undefined {
    const notification = this.notification();

    if (
      isVideoExportNotification(notification) ||
      isImageExportNotification(notification)
    ) {
      if (notification.type === NotificationType.Success) {
        return notification.content.link ?? '';
      } else if (notification.type === NotificationType.Error) {
        return;
      }
    } else if (
      isCommentAddedNotification(notification) ||
      isThreadArchivedNotification(notification) ||
      isThreadReopenedNotification(notification)
    ) {
      return getLinkToCreativeSet(notification.content.link);
    } else if (isAdScanningFinishedNotification(notification)) {
      return notification.content.link;
    } else if (
      isResourceAccessRequestedNotification(notification) ||
      isResourceAccessApprovedNotification(notification)
    ) {
      return notification.content.link;
    } else if (isPredictiveScoringCompletedNotification(notification)) {
      return notification.content.link;
    }
    return;
  }

  private computeLinkCaption(): string | undefined {
    const notification = this.notification();

    if (
      isVideoExportNotification(notification) ||
      isImageExportNotification(notification)
    ) {
      if (notification.type === NotificationType.Success) {
        return 'Download';
      } else if (notification.type === NotificationType.Error) {
        return 'More info';
      }
    } else if (
      isCommentAddedNotification(notification) ||
      isThreadReopenedNotification(notification) ||
      isThreadArchivedNotification(notification)
    ) {
      // TODO: uncomment this when we add the functionality
      // return 'Unfollow this thread'
      return;
    } else if (isAdScanningFinishedNotification(notification)) {
      return 'See results';
    } else if (isResourceAccessRequestedNotification(notification)) {
      return 'Open permissions';
    } else if (isResourceAccessApprovedNotification(notification)) {
      return 'Open creative set';
    } else if (isPredictiveScoringCompletedNotification(notification)) {
      return getPredictiveScoringLinkCaption(notification);
    }

    return;
  }

  private computeTimeSince(): string {
    const notification = this.notification();

    const date = new Date(notification.created);
    return timeSince(date);
  }

  private computeCreatedTime(): string {
    const notification = this.notification();
    const date = new Date(notification.created);
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };

    return date.toLocaleString(undefined, options);
  }

  private computeActorName(): string {
    const notification = this.notification();

    if (isCommentAddedNotification(notification)) {
      return notification.content.metadata.commenterName;
    } else if (isThreadArchivedNotification(notification)) {
      return notification.content.metadata.archiverName;
    } else if (isThreadReopenedNotification(notification)) {
      return notification.content.metadata.reopenerName;
    } else if (
      isResourceAccessRequestedNotification(notification) ||
      isResourceAccessApprovedNotification(notification) ||
      isResourceAccessRejectedNotification(notification)
    ) {
      return notification.sender.name ?? '';
    } else if (isSystemNotification(notification)) {
      return 'System';
    }
    return 'Unknown';
  }

  private computeAvatarText(): string {
    const name = this.actorName();
    const notification = this.notification();

    if (isResourceAccessRequestedNotification(notification)) {
      return this.getInitials(
        notification.content.metadata.inviteeFirstName,
        notification.content.metadata.inviteeLastName
      );
    } else if (isResourceAccessApprovedNotification(notification)) {
      return this.getInitials(
        notification.content.metadata.approverFirstName,
        notification.content.metadata.approverLastName
      );
    } else if (isResourceAccessRejectedNotification(notification)) {
      return this.getInitials(
        notification.content.metadata.rejecterFirstName,
        notification.content.metadata.rejecterLastName
      );
    }

    return name === 'Unknown' ? '?' : name;
  }

  private getInitials(firstName: string, lastName: string): string {
    const firstInitial = firstName?.charAt(0) || '';
    const lastInitial = lastName?.charAt(0) || '';
    return `${firstInitial}${lastInitial}`.toUpperCase();
  }

  private computeActionIcon(): UINUIIcon {
    const notification = this.notification();

    if (isCommentAddedNotification(notification)) {
      return 'forum';
    } else if (isThreadArchivedNotification(notification)) {
      return 'archive';
    } else if (isThreadReopenedNotification(notification)) {
      return 'unarchive';
    } else if (isAdScanningFinishedNotification(notification)) {
      return 'pest_control';
    } else if (
      isVideoExportNotification(notification) ||
      isImageExportNotification(notification)
    ) {
      return 'deployed_code';
    } else if (isResourceAccessRequestedNotification(notification)) {
      return 'passkey';
    } else if (isResourceAccessApprovedNotification(notification)) {
      return 'person_check';
    } else if (isResourceAccessRejectedNotification(notification)) {
      return 'person_cancel';
    } else if (isAiVideoGenerationNotification(notification)) {
      return 'auto_awesome';
    } else if (isPredictiveScoringCompletedNotification(notification)) {
      return 'query_stats';
    }
    return 'notifications';
  }

  private computeDescriptionContent(): string | undefined {
    const notification = this.notification();

    if (isCommentAddedNotification(notification)) {
      return (
        this.domSanitizer.sanitize(
          SecurityContext.HTML,
          notification.content.metadata.commentText
        ) ?? undefined
      );
    }
    if (isAiVideoGenerationNotification(notification)) {
      return getAiVideoGenerationDescriptionContent(notification);
    }
    if (isImageExportNotification(notification)) {
      return getImageExportDescription(notification);
    }
    if (isVideoExportNotification(notification)) {
      return getVideoExportDescription(notification);
    }
    return undefined;
  }

  private openLinkInNewTab(link: string, newTab = true): void {
    window.open(link, newTab ? '_blank' : '_self')?.focus();
  }
}
